/**
 * Data Quality — the assurance strip at the end of the company Overview tab.
 *
 * This is a SUMMARY, not the report. It answers one question — "can I trust the
 * figures above, and from which source?" — and hands everything else to the
 * Data Quality console, which has the room for it. The strip used to carry the
 * issue list, the statements table and a 140-field matrix as well, which made
 * the last section of Overview taller than every other section combined.
 *
 * Contract rules honoured here:
 *   - ADVISORY: never blocks the page, never changes a number, never reads as
 *     an error state for the page.
 *   - `headline` and `quality_score` are shown as given — never recomputed.
 *   - Chains are shown SIDE BY SIDE, one card per source, never merged or
 *     averaged: they validate different data and can legitimately disagree.
 *     API first — the researched chain is the baseline and a PDF upload is the
 *     addition. Every source's score is legible without a click.
 *   - The header carries a COUNT of sources, never a blended score.
 *   - A 404 (nothing stored) renders nothing at all — not an error, not a toast.
 */

import { useMemo } from 'react';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import Card, { CardBody, CardHeader } from '../ui/Card';
import LoadingCard from '../ui/LoadingCard';
import SourceScorecard from './dataQuality/SourceScorecard';
import { chainKey, orderChains } from './dataQuality/dqSelectors';
import { useValidationSummary } from './hooks/useDataQuality';

interface DataQualityPanelProps {
  /** The company detail page's id, e.g. "ind_DABUR". */
  companyId: string | null | undefined;
  /** Opens the Data Quality tab. Omitted, the cards are not clickable. */
  onOpenConsole?: () => void;
}

export default function DataQualityPanel({
  companyId,
  onOpenConsole,
}: DataQualityPanelProps) {
  // Same arguments as the console's call, so React Query serves both from one
  // request rather than firing twice for the same payload.
  const { data, isLoading, isError } = useValidationSummary(companyId);

  const ordered = useMemo(() => orderChains(data?.chains ?? []), [data?.chains]);

  if (!companyId) return null;

  if (isLoading) {
    return <LoadingCard title="Data Quality" message="Checking data quality…" height={120} />;
  }

  // A 404 resolves to null in the API layer, and a genuine failure of an
  // advisory panel is not worth an error state on the page.
  if (isError || ordered.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-neutral-900">Data Quality</h2>
            <p className="mt-0.5 text-xs text-neutral-500">
              Automated checks on the underlying figures — advisory only
            </p>
          </div>
          {/* A COUNT of sources, never a blended score. The chains measure
              different bodies of data, so the only honest number the header can
              carry about both is how many were checked. */}
          <span className="ml-auto shrink-0 text-xs text-neutral-500">
            {ordered.length} source{ordered.length === 1 ? '' : 's'} checked
          </span>
        </div>
      </CardHeader>

      <CardBody>
        <div
          className={`grid gap-4 ${
            ordered.length > 1 ? 'md:grid-cols-2' : 'grid-cols-1'
          }`}
        >
          {ordered.map((chain) => (
            <SourceScorecard key={chainKey(chain)} chain={chain} onOpen={onOpenConsole} />
          ))}
        </div>

        {onOpenConsole && (
          <button
            type="button"
            onClick={onOpenConsole}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors hover:border-primary-300 hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
          >
            Open data quality console
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        )}

        {/* Stated rather than left to be inferred: the report is stored, and
            when it is not, the console cannot show a run-over-run comparison. */}
        {data?.persistence_enabled === false && (
          <p className="mt-3 text-xs text-neutral-500">
            Results are not being stored for this deployment, so no comparison
            with a previous run is available.
          </p>
        )}
      </CardBody>
    </Card>
  );
}
